import { useEffect, useState, useMemo, useCallback } from 'react';

import { Timestamp } from 'firebase/firestore';

import { useNavigate } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Slide from '@mui/material/Slide';
import { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import AcUnitIcon from '@mui/icons-material/AcUnit';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';

import { useData } from '../../contextProviders/DataProvider';
import useNotices from '../../dataHooks/useNotices';
import { NoticeType } from '../../types/Notice';
import { DisplayDate } from '../../utils/utils';


interface GratiNoticeProps {
  sx?: SxProps<Theme>;
}

export default function GratiNoticeBanner(props: GratiNoticeProps) {
  const { userState } = useData();
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSliding, setIsSliding] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('left');
  const [noticeVisible, setNoticeVisible] = useState<('none' | 'sliding' | 'block')[]>([]);

  const { notices: allNotices, isNoticesLoading, noticesError } = useNotices(
    userState.status === 'ready' ? { contextPairs: null, filter: 'all' } : {}
  );

  // Filter to only active notices
  const notices = useMemo(() => {
    if (!allNotices) return null;
    const now = new Date();
    return allNotices.filter((notice) => {
      const startDate = (notice.displayStartsAt as Timestamp).toDate();
      const endDate = (notice.displayStopsAt as Timestamp).toDate();
      return now >= startDate && now <= endDate;
    });
  }, [allNotices]);

  useEffect(() => {
    if (notices && notices.length > 0) {
      setNoticeVisible(['block', ...Array(notices.length - 1).fill('none')]);
      setCurrentIndex(0); // Reset to first notice when notices change
    }
  }, [notices]);

  useEffect(() => {
    if (isSliding === false) {
      setSlideDirection((prev) => (prev === 'left' ? 'right' : 'left'));
      setNoticeVisible((prev) => {
        const newNoticeVisible = [...prev];
        newNoticeVisible[currentIndex] = 'block';
        return newNoticeVisible;
      });
    }
  }, [isSliding, currentIndex]);

  const handleNext = useMemo(() => () => {
    if (!notices) return;
    // First slide current alert out to the left
    setSlideDirection('left');
    setIsSliding(true);
    setCurrentIndex((prev) => {
      const newIndex = (prev + 1) % notices.length;
      setNoticeVisible((prevNotices) => {
        const newNoticeVisible = [...prevNotices];
        newNoticeVisible[prev] = 'sliding';
        return newNoticeVisible;
      });
      return newIndex;
    });
  }, [notices]);

  const handlePrev = useMemo(() => () => {
    if (!notices) return;
    // First slide current alert out to the right
    setSlideDirection('right');
    setIsSliding(true);
    setCurrentIndex((prev) => {
      const newIndex = (prev - 1 + notices.length) % notices.length;
      setNoticeVisible((prevNotices) => {
        const newNoticeVisible = [...prevNotices];
        newNoticeVisible[prev] = 'sliding';
        return newNoticeVisible;
      });
      return newIndex;
    });
  }, [notices]);

  const handleExited = useCallback((index: number) => {
    setIsSliding(false);
    setNoticeVisible((prevNotices) => {
      const newNoticeVisible = [...prevNotices];
      newNoticeVisible[index] = 'none';
      return newNoticeVisible;
    });
  }, []);

  const getAlertIcon = useCallback((noticeType: string) => {
    switch (noticeType) {
      case 'announcement':
        return <InfoIcon />;
      case 'alert' as NoticeType:
        return <WarningIcon />;
      case 'calendar' as NoticeType:
        return <CalendarMonthIcon />;
      case 'error' as NoticeType:
        return <ErrorIcon />;
      case 'frostdelay' as NoticeType :
        return <AcUnitIcon />;
      default:
        return <InfoIcon />;
    }
  }, []);

  const handleBannerClick = useCallback(() => {
    navigate('/notices');
  }, [navigate]);

  if (isNoticesLoading) {
    return <></>;
  }

  if (noticesError) {
    return <></>;
  }

  if (!notices || notices.length === 0) return <></>;

  return (
    <Box 
      sx={{ 
        ...props.sx,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center'
      }}
      onClick={handleBannerClick}
    >
      {notices.length > 1 && (
        <IconButton 
          onClick={(e) => {
            e.stopPropagation();
            handlePrev();
          }} 
          sx={{ color: 'primary.onContainer' }}
        >
          <ChevronLeftIcon />
        </IconButton>
      )}
      {notices.map((notice, index) => (
        <Box
          key={notice.itemId}
          sx={{
            display: noticeVisible[index] === 'none' ? 'none' : 'block',
            width: '100%',
            height: '100%',
          }}
        >
          <Slide
            direction={slideDirection}
            in={noticeVisible[index] === 'block'}
            timeout={500}
            onExited={() => handleExited(index)}
          >
            <Alert
              icon={getAlertIcon(notice.type)}
              severity="info"
              sx={{
                display: 'flex',
                flexGrow: 1,
                py: 0,
                pr: 0,
                mr: 0,
                width: '100%',
                backgroundColor: 'transparent',
                color: 'primary.onContainer',
                '& .MuiAlert-icon': { 
                  color: 'primary.onContainer',
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center'
                },
                position: 'relative',
              }}
            >
              <AlertTitle 
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {`${['calendar', 'frostdelay'].includes(notice.type) ? 
                  `${DisplayDate((notice.displayStopsAt as Timestamp).toDate(), 'America/Los_Angeles')}: ` : 
                  ''}${notice.title}`
                }
              </AlertTitle>
              <Typography 
                variant="bodyMedium"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {notice.text}
              </Typography>
            </Alert>
          </Slide>
        </Box>
      ))}

      {notices.length > 1 && (
        <IconButton 
          onClick={(e) => {
            e.stopPropagation();
            handleNext();
          }} 
          sx={{ color: 'primary.onContainer' }}
        >
          <ChevronRightIcon />
        </IconButton>
      )}
    </Box>
  );
}
